import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IUser } from '@interfaces/IUser';
import { GetProfileRequest } from '@myStore/profile-view/profile-view.actions';
import { selectProfile } from '@myStore/profile-view/profile-view.selector';
import { GetUnconfirmedCount } from '@myStore/users/user.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AuthService } from '@services/auth/auth.service';
import { UsersService } from '@services/users/users.service';
import jwt_decode from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAppState } from '../app.state';
import {
  EAuthActions,
  GetTokenValidity,
  GetTokenValiditySuccess,
  Login,
  LoginSuccess,
  Logout,
  LogoutSuccess,
  Refresh,
  RequestFailure,
  ResetPassword,
  ResetPasswordRequest,
  ResetPasswordRequestSuccess,
} from './auth.actions';
import { ITokens } from './auth.state';

@Injectable()
export class AuthEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _authService: AuthService,
    private _usersService: UsersService,
    private _router: Router,
    private _toastr: ToastrService
  ) {}

  updateTokens(userTokens: ITokens) {
    if (this._authService.getToken('refresh')) {
      const refreshToken = this._authService.getToken('refresh');
      localStorage.setItem(
        'tokens',
        JSON.stringify({ ...userTokens, refresh: refreshToken })
      );
    } else {
      localStorage.setItem('tokens', JSON.stringify(userTokens));
    }
  }

  updateUser = (userTokens: ITokens) => {
    return new Promise(resolve => {
      this.updateTokens(userTokens);
      const decodeUser: IUser = jwt_decode(userTokens.access);
      const user: IUser = decodeUser;
      localStorage.setItem('exp', JSON.stringify(decodeUser.exp));
      if (user.user_id) {
        this._store.dispatch(new GetProfileRequest(+user.user_id));
      }

      const profileSub = this._store
        .pipe(select(selectProfile))
        .subscribe(profile => {
          if (profile) {
            this._store.dispatch(new LoginSuccess(profile));

            if (profile.groups?.includes('admins') || profile.is_superuser) {
              this._store.dispatch(new GetUnconfirmedCount());
            }

            profileSub.unsubscribe();
            resolve(true);
          }
        });
    });
  };

  Login$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<Login>(EAuthActions.Login),
        map(action => action.userData),
        switchMap(payload => {
          return this._authService
            .login({ email: payload.email, password: payload.password })
            .pipe(
              map(async (userTokens: ITokens) => {
                await this.updateUser(userTokens);
                await this._router.navigate(['/']);
              }),
              catchError(err => {
                this._store.dispatch(new RequestFailure(err.error));
                return of(new RequestFailure(err.error));
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  Logout$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<Logout>(EAuthActions.Logout),
        switchMap(() => {
          return this._authService.logout().pipe(
            map(() => {
              this._store.dispatch(new LogoutSuccess());
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  LogoutSuccess$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<LogoutSuccess>(EAuthActions.LogoutSuccess),
        map(props => {
          localStorage.clear();
          this._router.navigate(['/login']);
          return props;
        })
      );
    },
    { dispatch: false }
  );

  Refresh$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<Refresh>(EAuthActions.Refresh),
        switchMap(() => {
          return this._authService.refreshToken().pipe(
            map((userTokens: ITokens) => {
              this.updateTokens(userTokens);
              return userTokens;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  resetPasswordRequest$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ResetPasswordRequest>(EAuthActions.ResetPasswordRequest),
        map(action => action.email),
        switchMap(email => {
          return this._authService.resetPasswordRequest(email).pipe(
            map(res => {
              this._store.dispatch(new ResetPasswordRequestSuccess(res.detail));
            }),
            catchError(err => {
              this._store.dispatch(new RequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  GetTokenValidity$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetTokenValidity>(EAuthActions.GetTokenValidity),
        switchMap(payload => {
          return this._authService.getTokenValidity(payload.token).pipe(
            map(() => {
              this._store.dispatch(new GetTokenValiditySuccess());
            }),
            catchError(err => {
              this._store.dispatch(
                new RequestFailure({ detail: 'Данный токен недействителен' })
              );
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  ResetPassword$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ResetPassword>(EAuthActions.ResetPassword),
        switchMap(action => {
          return this._authService.resetPassword(action.passwordData).pipe(
            map(() => {
              this._router.navigate(['/login']);
              this._toastr.success('Пароль успешно изменен');
            }),
            catchError(err => {
              this._store.dispatch(new RequestFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
